import { useState, useRef, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';

export default function ScreenshotGallery({ screenshots, avatar, frame }) {
  const { theme } = useTheme();
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef(null);
  const slideRefs = useRef([]);

  const getImage = (shot) =>
    theme === 'dark' && shot.darkImage ? shot.darkImage : shot.image;

  // Track which slide is visible via IntersectionObserver (scroll mode only)
  useEffect(() => {
    if (frame) return;
    const container = scrollRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = slideRefs.current.indexOf(entry.target);
            if (index !== -1) setActiveIndex(index);
          }
        });
      },
      { root: container, threshold: 0.6 }
    );

    slideRefs.current.forEach((slide) => {
      if (slide) observer.observe(slide);
    });

    return () => observer.disconnect();
  }, [screenshots.length, frame]);

  const scrollTo = useCallback((index) => {
    const clamped = Math.max(0, Math.min(index, screenshots.length - 1));
    if (frame) {
      setActiveIndex(clamped);
    } else {
      slideRefs.current[clamped]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });
    }
  }, [screenshots.length, frame]);

  const prev = () => scrollTo(activeIndex - 1);
  const next = () => scrollTo(activeIndex + 1);

  const activeShot = screenshots[activeIndex];

  return (
    <div className="relative group">
      {/* Avatar - overlapping bottom-left of gallery, above text area */}
      {avatar && (
        <img
          src={avatar}
          alt=""
          className="absolute bottom-24 sm:bottom-28 -left-1 sm:left-0 w-44 h-44 sm:w-56 sm:h-56 object-contain drop-shadow-lg pointer-events-none z-20"
        />
      )}

      {/* Image area */}
      <div className={`relative overflow-hidden ${frame ? '' : 'rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-surface-card-dark'}`}>

        {frame ? (
          /* Framed mode: single image swap with crossfade */
          <div className="relative aspect-video bg-slate-100 dark:bg-surface-dark">
            {screenshots.map((shot, i) => (
              <img
                key={`${shot.id}-${theme}`}
                src={getImage(shot)}
                alt={shot.title}
                className={`absolute inset-0 w-[94%] h-[94%] mx-auto mt-[1.5%] object-cover transition-opacity duration-300 ${
                  i === activeIndex ? 'opacity-100' : 'opacity-0'
                }`}
                loading="lazy"
              />
            ))}
            {/* Static frame overlay */}
            <img
              src={frame}
              alt=""
              className="absolute inset-0 w-full h-full pointer-events-none z-10"
            />
          </div>
        ) : (
          /* No-frame mode: scrollable track */
          <div
            ref={scrollRef}
            className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {screenshots.map((shot, i) => (
              <div
                key={shot.id}
                ref={(el) => (slideRefs.current[i] = el)}
                className="snap-start shrink-0 w-full"
              >
                <div className="aspect-video bg-slate-100 dark:bg-slate-800 overflow-hidden">
                  <img
                    src={getImage(shot)}
                    alt={shot.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML =
                        '<div class="w-full h-full flex items-center justify-center text-slate-400 dark:text-slate-600 text-sm">Screenshot coming soon</div>';
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Previous / Next arrows */}
        {screenshots.length > 1 && (
          <>
            <button
              onClick={prev}
              disabled={activeIndex === 0}
              aria-label="Previous screenshot"
              className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/80 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 shadow-sm flex items-center justify-center text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-700 transition-all disabled:opacity-0 disabled:pointer-events-none opacity-0 group-hover:opacity-100 focus:opacity-100"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                <path fillRule="evenodd" d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clipRule="evenodd" />
              </svg>
            </button>
            <button
              onClick={next}
              disabled={activeIndex === screenshots.length - 1}
              aria-label="Next screenshot"
              className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/80 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 shadow-sm flex items-center justify-center text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-700 transition-all disabled:opacity-0 disabled:pointer-events-none opacity-0 group-hover:opacity-100 focus:opacity-100"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                <path fillRule="evenodd" d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
              </svg>
            </button>
          </>
        )}
      </div>

      {/* Text area below - updates based on active slide */}
      <div className="p-5">
        <h3 className="font-semibold text-slate-900 dark:text-white mb-1.5">
          {activeShot.title}
        </h3>
        <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
          {activeShot.description}
        </p>
      </div>

      {/* Dot indicators */}
      {screenshots.length > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {screenshots.map((shot, i) => (
            <button
              key={shot.id}
              onClick={() => scrollTo(i)}
              aria-label={`Go to ${shot.title}`}
              className={`w-2 h-2 rounded-full transition-all ${
                i === activeIndex
                  ? 'bg-primary-500 w-6'
                  : 'bg-slate-300 dark:bg-slate-600 hover:bg-slate-400 dark:hover:bg-slate-500'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
